import { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { PersonAdd } from '@mui/icons-material';
import IosShareIcon from '@mui/icons-material/IosShare';
import { Box, Button, Tab, Tabs, Typography } from '@mui/material';
import {
  Estate_UpdateEstateInput,
  useEstate_GetEstateByIdQuery,
  useEstate_UpdateEstateMutation,
  useEstateTaskInstance_GetEstateTaskInstanceCompletionPercentageByEstateIdQuery,
  useFeatureSubscription_GetFeatureSubscriptionTypeQuery,
  User_InvitePortalClientMutation,
} from 'queries';

import LinearWithValueLabel from 'common/progress';
import Spinner from 'common/spinner';
import HomeLayout from 'layouts/home-layout';
import ClientShareModal from 'pages/common/client/client-share-modal';
import CreatePaymentModal from 'pages/common/estate/create-payment-modal';
import PortalUserDisplay from 'pages/common/portal/portal-user-display';
import PortalUsersAutocomplete from 'pages/common/portal/portal-users-autocomplete';
import { UserAutoCompleteOption } from 'pages/common/portal/portal-users-autocomplete';
import EstateBalanceSheetPage from 'pages/estate-balance-sheet-page';
import EstateTrusuiteSearchPaymentForm from 'pages/estate-checklist/components/estate-trusuite-search-payment-form';
import { EstateChecklist } from 'pages/estate-checklist/estate-checklist';
import EstateResultsPage from 'pages/estate-results-page';
import EstateVault from 'pages/estate-vault';
import { isEstateEditable } from 'utils/estate';
import { canAddOrEditClientMatter, canAssignTasks } from 'utils/feature-checker';
import { NotificationSeverity, showAlert, showToast } from 'utils/notification-manager';

import EditEstateModal from '../estates/components/edit-estate-modal';

import { useStyles } from './estate-dashboard.styles';

export enum EstateDashboardTab {
  ESTATE_CHECKLIST = '0',
  VAULT = '1',
  BALANCE_SHEET = '2',
  SEARCH_RESULTS = '3',
}

export const EstateDashboard = () => {
  const classes = useStyles();
  const location = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(location.search);
  const queryTab = queryParams.get('tab');
  const { portalId, estateId } = useParams<Record<string, string>>();

  const [showPointPersonSelection, setshowPointPersonSelection] = useState(false);
  const [editingEstateId, setEditingEstateId] = useState<string | undefined>();
  const [showEstatePaymentModal, setShowEstatePaymentModal] = useState(false);
  const [showClientShareModal, setShowClientShareModal] = useState(false);
  const initialTab = queryTab ? parseInt(queryTab, 10) : EstateDashboardTab.ESTATE_CHECKLIST;
  const [tabValue, setTabValue] = useState(initialTab);
  const PointPersonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const updatedTab = params.get('tab');
    if (!updatedTab) {
      params.set('tab', EstateDashboardTab.ESTATE_CHECKLIST);
      history.replace({ search: params.toString() });
      setTabValue(0);
    } else if (parseInt(updatedTab, 10) !== tabValue) {
      setTabValue(parseInt(updatedTab, 10));
    }
  }, [location.search, tabValue, history]);

  const { data: estateData, loading: estateLoading } = useEstate_GetEstateByIdQuery({
    variables: { id: estateId },
    onError: (e) => {
      showAlert({
        message: 'Error getting estate by ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const { data: featureSubscriptionTypeData } =
    useFeatureSubscription_GetFeatureSubscriptionTypeQuery({
      variables: {
        portalId,
      },
      onError: (e) => {
        showAlert({
          message: 'Error getting feature subscription type: ' + e.message,
          severity: NotificationSeverity.ERROR,
        });
      },
    });

  const {
    data: estateTaskInstanceCompletionPercentage,
    loading: estateTaskInstanceCompletionPercentageLoading,
    refetch: refetchEstateTaskInstanceCompletionPercentage,
  } = useEstateTaskInstance_GetEstateTaskInstanceCompletionPercentageByEstateIdQuery({
    variables: {
      input: {
        portalId,
        estateId,
      },
    },
    onError: (e) => {
      showAlert({
        message: 'Error getting estate task completion percentage by estate ID: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const handleShareComplete = (invitePortalClientData: User_InvitePortalClientMutation) => {
    if (invitePortalClientData.data.emailVerified) {
      showToast({
        message: `Client now has shared access to this estate`,
        severity: NotificationSeverity.SUCCESS,
      });
    } else {
      showToast({
        message: `Share invitation has been sent to client email successfully`,
        severity: NotificationSeverity.SUCCESS,
      });
    }
    setShowClientShareModal(false);
  };

  const selectPointPerson = () => {
    setshowPointPersonSelection(true);
  };

  const [updateEstate] = useEstate_UpdateEstateMutation({
    onError: (e) => {
      showAlert({
        message: 'Error updating estate: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });

  const isLoading = estateLoading || estateTaskInstanceCompletionPercentageLoading;

  if (isLoading) {
    return <Spinner />;
  }

  const estate = estateData!.data;

  const onEditClientMatter = () => {
    if (!canAddOrEditClientMatter(featureSubscriptionTypeData!.data)) {
      showToast({
        message: 'Please upgrade your account to edit this client/matter.',
        severity: NotificationSeverity.WARNING,
      });
      return;
    }

    setEditingEstateId(estate.id);
  };

  const onOrderSearchButtonClick = () => {
    if (isEstateEditable(estate!.status)) {
      if (!canAssignTasks(featureSubscriptionTypeData!.data)) {
        showToast({
          message: 'Please upgrade your account to run this search.',
          severity: NotificationSeverity.WARNING,
        });
        return;
      }

      setEditingEstateId(undefined);
      setShowEstatePaymentModal(true);
    }
  };

  const handleAssignedUserChange = (assignedUserId: string | null) => {
    const updateEstateInput: Estate_UpdateEstateInput = {
      id: estate!.id,
      portalId,
    };

    updateEstateInput.assignedUserId = assignedUserId;

    updateEstate({
      variables: {
        input: updateEstateInput,
      },
    });
    setshowPointPersonSelection(false);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    const params = new URLSearchParams(location.search);
    params.set('tab', newValue.toString());
    history.replace({ pathname: location.pathname, search: params.toString() });
  };

  const showPointPerson = estate.assignedUserId && !showPointPersonSelection;

  const taskCompletionPercentage = estateTaskInstanceCompletionPercentage?.data;

  return (
    <Box className={classes.mainContainer}>
      <HomeLayout>
        <Box className={classes.main}>
          {editingEstateId && (
            <EditEstateModal
              onExitClick={() => setEditingEstateId(undefined)}
              estateId={editingEstateId}
              onOrderSearchButtonClick={onOrderSearchButtonClick}
            />
          )}
          <CreatePaymentModal
            paymentForm={
              <EstateTrusuiteSearchPaymentForm
                setShowModal={setShowEstatePaymentModal}
                estate={estate}
              />
            }
            setShowModal={setShowEstatePaymentModal}
            showModal={showEstatePaymentModal}
          />
          {estate &&
            estate.id &&
            estate.estateName &&
            estate.estateClientFirstName &&
            estate.estateClientLastName &&
            estate.estateClientEmailAddress &&
            estate.estateClientPhoneNumber && (
              <ClientShareModal
                estateId={estate.id}
                estateName={estate.estateName}
                clientEmailAddress={estate.estateClientEmailAddress}
                showModal={showClientShareModal}
                setShowModal={setShowClientShareModal}
                onShareComplete={handleShareComplete}
              />
            )}
          <Box className={classes.right}>
            <Box className={classes.header}>
              <Box className={classes.container}>
                <Box>
                  <Box>
                    <Typography className={classes.estateName}>
                      {estate?.estateName || ''}
                    </Typography>
                  </Box>
                  <Box
                    ref={PointPersonRef}
                    className={classes.pointPerson}
                    onClick={selectPointPerson}
                  >
                    {showPointPersonSelection && (
                      <Box className={classes.pointPersonSelection}>
                        <PortalUsersAutocomplete
                          elementId="assignedUserId"
                          portalId={portalId}
                          selectedUserId={
                            estate?.assignedUserId ? estate?.assignedUserId.toString() : undefined
                          }
                          handleUserChange={(
                            _elementId: string,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            _e: any,
                            option: UserAutoCompleteOption | null,
                          ) => handleAssignedUserChange(option?.id || null)}
                          handleLoseFocus={() => setshowPointPersonSelection(false)}
                          openByDefault={true}
                        />
                      </Box>
                    )}
                    <Box>
                      {showPointPerson && (
                        <PortalUserDisplay userId={estate.assignedUserId!} showName={false} />
                      )}{' '}
                      {!estate?.assignedUserId && !showPointPersonSelection && (
                        <PersonAdd className={classes.pointPersonDisplay} />
                      )}
                    </Box>
                  </Box>
                </Box>
                <Box className={classes.flexItemRight}>
                  <Button
                    className={classes.button}
                    color="primary"
                    size="small"
                    onClick={onEditClientMatter}
                  >
                    Edit Client Name/Matter
                  </Button>
                  <IosShareIcon
                    className={classes.shareIcon}
                    onClick={() => {
                      if (
                        !estate?.estateClientFirstName ||
                        !estate?.estateClientLastName ||
                        !estate?.estateClientEmailAddress ||
                        !estate?.estateClientPhoneNumber
                      ) {
                        showAlert({
                          message:
                            'Cannot share to client because client first name last name, email address, and phone number are all required',
                          severity: NotificationSeverity.ERROR,
                        });
                        return;
                      }

                      setShowClientShareModal(true);
                    }}
                  />
                </Box>
              </Box>
              <Box className={classes.progressBar}>
                <Typography>Checklist Progress</Typography>
                <LinearWithValueLabel progress={taskCompletionPercentage!} showNumber={false} />
              </Box>
              <Box className={classes.tabBox}>
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  aria-label="Estate tabs"
                  variant="scrollable"
                  scrollButtons="auto"
                  className={classes.tabSection}
                >
                  <Tab
                    label="Estate Checklist"
                    id="tab-0"
                    aria-controls="tab-panel-0"
                    className={classes.tab}
                  />
                  <Tab
                    label="Vault"
                    id="tab-1"
                    aria-controls="tab-panel-1"
                    className={classes.tab}
                  />
                  <Tab
                    label="Balance Sheet"
                    id="tab-2"
                    aria-controls="tab-panel-2"
                    className={classes.tab}
                  />
                  <Tab
                    label="Search Results"
                    id="tab-3"
                    aria-controls="tab-panel-3"
                    className={classes.tab}
                  />
                </Tabs>
              </Box>
            </Box>
            <Box className={classes.tabContent}>
              <Box className={classes.estateDashboardWrapper}>
                {tabValue === parseInt(EstateDashboardTab.ESTATE_CHECKLIST) && (
                  <EstateChecklist
                    hideNav={true}
                    refetchEstateTaskInstanceCompletionPercentage={
                      refetchEstateTaskInstanceCompletionPercentage
                    }
                  />
                )}
              </Box>
              <Box className={classes.estateVaultWrapper}>
                {tabValue === parseInt(EstateDashboardTab.VAULT) && (
                  <EstateVault hideNav={true} folderId={estate.documentsFolderId!} />
                )}
              </Box>
              <Box className={classes.estateBalanceSheetWrapper}>
                {tabValue === parseInt(EstateDashboardTab.BALANCE_SHEET) && (
                  <div>
                    <EstateBalanceSheetPage hideNav={true} />
                  </div>
                )}
              </Box>
              <Box className={classes.estateResultsPageWrapper}>
                {tabValue === parseInt(EstateDashboardTab.SEARCH_RESULTS) && (
                  <EstateResultsPage hideNav={true} />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </HomeLayout>
    </Box>
  );
};
